import { MutableRefObject } from "react";
import { Button, Modal } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { apiOrganization } from "src/services/Api/Organization/apiOrganization";
import { ProductContactApi } from "src/services/Api/Organization/types";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import { MoneyApi } from "go-core/api/types";
import FormMoneyInput from "go-form/components/FormMoneyInput";
import { FormSelectGroup } from "go-form/components/FormSelect";
import { useConfirmation } from "go-form/components/ModalConfirm";
import { selectOrganization } from "go-security/services/organizations/selectors";
import { ContactModalState } from "../../../services/types";

interface Props {
	contactModalState: ContactModalState;
	setContactModalState: (contactModalState: ContactModalState) => void;
	contactModalDataRef: MutableRefObject<ProductContactApi | undefined>;
	contactModalData?: ProductContactApi;
	productId: number;
	onRefresh: () => Promise<void>;
}

interface AssignContactToProductApi {
	reference_price: MoneyApi;
	contact_id: number;
}

export const ProductContactModal = ({
	contactModalState,
	setContactModalState,
	contactModalDataRef,
	productId,
	onRefresh,
	contactModalData,
}: Props) => {
	const form = useForm<AssignContactToProductApi>({
		defaultValues: {
			contact_id: contactModalData?.contact.id,
			reference_price: contactModalData?.reference_price,
		},
	});
	const { currency } = useSelector(selectOrganization);
	const formCurrency = currency ?? "PLN";
	const { addFlash, addSuccessFlash } = useFlash();
	const confirmation = useConfirmation();
	const {
		control,
		formState: { errors },
		handleSubmit,
		setValue,
	} = form;
	const { t } = useTranslation();
	const disableContactsInput = contactModalState === ContactModalState.UPDATE;

	const createOrUpdateContactButtonTranslation =
		contactModalState === ContactModalState.NEW
			? t("common.action.add", { ns: "lib" })
			: t("common.action.save", { ns: "lib" });

	const contactInputDefaultValue = contactModalData
		? {
				id: contactModalData.contact.id,
				label: contactModalData.contact.name,
		  }
		: undefined;

	const displayRemoveButton = contactModalState === ContactModalState.UPDATE;

	const onSearchContacts = async (query: string, params: Record<string, any>, options?: Record<string, any>) => {
		return apiOrganization.getContactSearchSelect(query, params, {
			cancelToken: options?.token,
		});
	};

	const hideModal = async (refresh = false) => {
		if (contactModalDataRef?.current) contactModalDataRef.current = undefined;
		if (refresh) await onRefresh();
		setContactModalState(ContactModalState.CLOSED);
	};

	const deleteContactHandler = async () => {
		if (!contactModalData) return;

		try {
			await confirmation({
				title: t("confirmation.title", { ns: "lib" }),
				message: t("confirmation.message.remove", { ns: "lib" }),
			});

			await apiOrganization.removeProductContact(productId, contactModalData.contact.id);
			addSuccessFlash(t("lib:common.flash.removed"));
			hideModal(true);
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const createOrUpdateContactHandler = handleSubmit(
		async ({ contact_id, reference_price }: AssignContactToProductApi) => {
			try {
				if (contactModalState === ContactModalState.UPDATE) {
					await apiOrganization.updateProductContact(productId, contact_id, reference_price);
				} else {
					await apiOrganization.createProductContact(productId, contact_id, reference_price);
				}
				addSuccessFlash(t("lib:common.flash.saved"));
				hideModal(true);
			} catch (err) {
				handleError.alert(err, addFlash);
			}
		}
	);

	return (
		<Modal show={true} onHide={hideModal}>
			<Modal.Header closeButton>
				<Modal.Title>{t("modules.contact.action.add_contact_to_product.title")}</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				<FormSelectGroup
					name="contact_id"
					label={t("common.word.contact")}
					control={control}
					defaultValue={contactInputDefaultValue}
					errors={errors}
					getOptionValue={(option) => option.id}
					loadOptions={onSearchContacts}
					disabled={disableContactsInput}
				/>
				<FormMoneyInput
					name="reference_price.amount"
					label={t("modules.product.field.default_price_net_money.title")}
					control={control}
					errors={errors}
					currency={formCurrency}
					defaultValue={contactModalData?.reference_price}
					onChange={(newAmount?: number) => {
						setValue("reference_price", { amount: newAmount ?? 0, currency: formCurrency });
					}}
				/>
			</Modal.Body>
			<Modal.Footer>
				<Button variant="light" onClick={() => hideModal()}>
					{t("common.action.close", { ns: "lib" })}
				</Button>
				{displayRemoveButton && (
					<Button variant="danger" onClick={deleteContactHandler}>
						{t("common.action.remove", { ns: "lib" })}
					</Button>
				)}
				<Button onClick={createOrUpdateContactHandler}>{createOrUpdateContactButtonTranslation}</Button>
			</Modal.Footer>
		</Modal>
	);
};
